import { LitElement, html, property } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { until } from 'lit-html/directives/until'
import { contextualize } from '@polleverywhere/cutlass'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import './style.scss'

const sizes = {
  compact: 'compact',
  cozy: 'cozy'
}

const themes = {
  blue: 'blue',
  white: 'white'
}

const types = {
  ghost: 'ghost',
  link: 'link',
  outline: 'outline',
  solid: 'solid'
}

/**
 * Button element lets consumers render an on-brand button that meets accessibility
 * standards. Consumers may listen for click events as they normally would against
 * a `<button>` element and handle the interaction.
 *
 * Render the button.
 * `<pe-button></pe-button>`
 *
 * See stories for all variants.
 *
 * @element pe-button
 *
 * @prop {string} label - Label for the button
 * @prop {boolean} hideLabel - Flag for hiding label
 * @prop {string} description - Sets the ARIA label for this button, especially
 *  for the case when the button has no label.
 * @prop {string} icon - Name of icon glyph to display next to the label
 * @prop {string} name - Name of the button when submitted with form data.
 * @prop {string} size - Size of button, defaults to `cozy`. Possible values:
 *  `cozy`, `compact`.
 * @prop {string} theme - Theme of button for colors, defaults to `blue`.
 *  Possible values: 'blue', 'white'. To add more, see ./style.scss
 * @prop {string} type - Type of button, defaults to 'solid' when not set.
 *  Possible values: 'solid', 'outline', 'ghost', 'link'.
 * @prop {boolean} submit - Make this a "submit" button
 * @prop {boolean} disabled - Disables the button.
 *
 * @cssprop --pe-button-color - Text/outline color
 * @cssprop --pe-button-background-color - Background color
 * @cssprop --pe-button-hover-color - Hover text color
 * @cssprop --pe-button-hover-background-color - Hover background color
 * @cssprop --pe-button-active-color - Active text color
 * @cssprop --pe-button-active-background-color - Active background color
 */
export default class Button extends LitElement {
  static get tagName () { return 'pe-button' }

  @property({ type: String })
  label

  @property({ type: Boolean, attribute: 'hide-label' })
  hideLabel = false

  @property({ type: String })
  description

  @property({ type: String })
  icon

  @property({ type: String })
  name

  @property({ type: String, reflect: true })
  size = sizes.cozy

  @property({ type: String, reflect: true })
  theme = themes.blue

  @property({ type: String, reflect: true })
  type = types.solid

  @property({ type: Boolean, reflect: true })
  disabled = false

  @property({ type: Boolean, reflect: true })
  submit = false

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  createRenderRoot () { return this }

  render () {
    validateProperty.call(this, 'size')
    validateProperty.call(this, 'theme')
    validateProperty.call(this, 'type')

    return html`
    <button
        ?disabled=${this.disabled}
        aria-label=${ifDefined(this.description || this.label)}
        class=${bem('button')}
        name=${ifDefined(this.name)}
        type=${this.submit ? 'submit' : 'button'}>
      ${this.icon ? until(renderIcon.call(this)) : ''}
      ${renderLabel.call(this)}
    </button>`
  }
}

function validateProperty (name) {
  const validValues =
    (name === 'size' && Object.values(sizes)) ||
    (name === 'theme' && Object.values(themes)) ||
    (name === 'type' && Object.values(types))
  if (!validValues.includes(this[name])) {
    throw new Error(`'${name}' must be one of: ${validValues.join(',')}`)
  }
}

async function renderIcon () {
  await import(
    /* webpackChunkName: "pe-graphic" */
    '@polleverywhere/pe-graphic'
  )

  return html`<pe-graphic class=${bem('icon')} name=${this.icon} type='icon'></pe-graphic>`
}

function renderLabel () {
  if (!this.label || this.hideLabel) { return }

  return html`<span class=${bem('label')}>${this.label}</span>`
}

const bem = bemBlock(Button.tagName)
register(module, Button.tagName, Button)
